import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
// Use the `/pure` entry so we don't eagerly inject the Stripe.js <script>
// tag on every page load. Stripe.js is only loaded once a host actually
// opens the "Add card" dialog and we call loadStripe() ourselves.
// (The `/pure` subpath only re-exports `loadStripe`; the type lives on
// the main entry, which is type-only and has no runtime side effects.)
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreditCard, Plus, MoreVertical, Loader2, Trash2, Star, Wallet, AlertTriangle, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { apiRequest, queryClient, parseErrorMessage } from "@/lib/queryClient";
import { useLocation, Link } from "wouter";
import type { VendorCreditCard } from "@shared/schema";

// Task #1983 — Hosts land here from the Mercury skipped-action toast.
// Keep in sync with the Mercury connection settings page route.
const MERCURY_SETTINGS_PATH = "/app/financials/settings";

// Server augments each card with `needsReEntry` (true when the Stripe
// PaymentMethod is attached to a different customer than the card's
// host owner — typically a legacy row left over from the team-member
// card bug). Stripe doesn't allow moving a PM between customers, so the
// only fix is to remove the card and add it again.
type VendorCreditCardWithStatus = VendorCreditCard & { needsReEntry?: boolean };

import { getActiveStripePublishableKey } from "@/lib/stripe-publishable-key";

// Mirrors `getStripeConfigStatus()` on the server.
export type StripeMode = "test" | "live" | "unknown";
export type StripeConfig = {
  secretKeyConfigured: boolean;
  publishableKeyConfigured: boolean;
  enabled: boolean;
  ready: boolean;
  mode: StripeMode;
};

// Cache the Stripe.js promise across renders so we don't reload it.
let cachedStripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> | null {
  const key = getActiveStripePublishableKey();
  if (!key) return null;
  if (!cachedStripePromise) cachedStripePromise = loadStripe(key);
  return cachedStripePromise;
}

function formatCardLine(card: VendorCreditCard): string {
  const brand = card.brand ? card.brand.toUpperCase() : "Card";
  const last4 = card.last4 ?? "••••";
  return `${brand} •••• ${last4}`;
}

function formatExpiry(card: VendorCreditCard): string {
  if (!card.expMonth || !card.expYear) return "";
  const m = String(card.expMonth).padStart(2, "0");
  const y = String(card.expYear).slice(-2);
  return `Exp ${m}/${y}`;
}

function isExpired(card: VendorCreditCard): boolean {
  if (!card.expMonth || !card.expYear) return false;
  const now = new Date();
  return new Date(card.expYear, card.expMonth) < now;
}

function isExpiringSoon(card: VendorCreditCard): boolean {
  if (!card.expMonth || !card.expYear) return false;
  const now = new Date();
  const expDate = new Date(card.expYear, card.expMonth);
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return expDate > now && expDate <= thirtyDaysFromNow;
}

export function HostPaymentMethodsSection({ readOnly = false }: { readOnly?: boolean }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VendorCreditCard | null>(null);
  const [editTarget, setEditTarget] = useState<VendorCreditCard | null>(null);
  const [editNickname, setEditNickname] = useState("");

  const cardsQuery = useQuery<VendorCreditCardWithStatus[]>({
    queryKey: ["/api/app/billing/payment-methods"],
  });

  // Use the same readiness derivation as the page-level setup banner so
  // the inline "Add card" gate and the prominent banner cannot disagree.
  // The publishable side comes from the actual client bundle (not the
  // server's process.env view), and only the secret side is read from
  // the server probe.
  const stripeConfigQuery = useQuery<StripeConfig>({
    queryKey: ["/api/app/billing/stripe-config"],
  });
  const serverKeyMissing = stripeConfigQuery.data?.secretKeyConfigured === false;
  const clientPublishableMissing = !getActiveStripePublishableKey();
  const stripeReady = stripeConfigQuery.data
    ? !!stripeConfigQuery.data.secretKeyConfigured && !clientPublishableMissing
    : false;

  const setDefaultMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/app/billing/payment-methods/${id}`, { isDefault: true });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/payment-methods"] });
      toast({ title: "Default card updated" });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't set default", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  // Task #1957 — Server-attached warning when a Mercury-linked card action
  // couldn't actually be propagated to Mercury because the host's Mercury
  // connection is missing/inactive/expired. Same shape as Task #1940's
  // `mercuryCancelSkipped` on PATCH /api/app/properties/:id.
  type MercurySkippedWarning = {
    action: "rename" | "cancel";
    reason: "no_connection" | "inactive" | "no_api_key";
    message: string;
  };
  const extractMercuryWarning = (
    data: unknown,
    key: "mercuryRenameSkipped" | "mercuryCancelSkipped",
  ): MercurySkippedWarning | undefined => {
    if (!data || typeof data !== "object") return undefined;
    const warnings = (data as { warnings?: Record<string, unknown> }).warnings;
    if (!warnings || typeof warnings !== "object") return undefined;
    const w = warnings[key];
    if (!w || typeof w !== "object" || typeof (w as MercurySkippedWarning).message !== "string") return undefined;
    return w as MercurySkippedWarning;
  };

  // Task #1983 — Toast action lets the host jump straight to the Mercury
  // connection settings page so they can reconnect and bring PadPulse and
  // Mercury back in sync.
  const reconnectMercuryAction = (testId: string) => (
    <ToastAction
      altText="Reconnect Mercury"
      asChild
      data-testid={testId}
    >
      <Link href={MERCURY_SETTINGS_PATH}>Reconnect Mercury</Link>
    </ToastAction>
  );

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/app/billing/payment-methods/${id}`, undefined);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/payment-methods"] });
      setDeleteTarget(null);
      const warning = extractMercuryWarning(data, "mercuryCancelSkipped");
      if (warning) {
        toast({
          title: "Mercury card wasn't cancelled",
          description: warning.message,
          variant: "destructive",
          action: reconnectMercuryAction("button-toast-reconnect-mercury-cancel"),
        });
      } else {
        toast({ title: "Card removed" });
      }
    },
    onError: (e: any) => {
      toast({ title: "Couldn't remove card", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  const updateNicknameMutation = useMutation({
    mutationFn: async ({ id, nickname }: { id: number; nickname: string | null }) => {
      const res = await apiRequest("PATCH", `/api/app/billing/payment-methods/${id}`, { nickname });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/payment-methods"] });
      setEditTarget(null);
      setEditNickname("");
      const warning = extractMercuryWarning(data, "mercuryRenameSkipped");
      if (warning) {
        toast({
          title: "Mercury card wasn't renamed",
          description: warning.message,
          variant: "destructive",
          action: reconnectMercuryAction("button-toast-reconnect-mercury-rename"),
        });
      } else {
        toast({ title: "Nickname updated" });
      }
    },
    onError: (e: any) => {
      toast({ title: "Couldn't update nickname", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  const cards = cardsQuery.data ?? [];
  const sorted = useMemo(() => {
    return [...cards].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
  }, [cards]);

  const openEditNickname = (card: VendorCreditCard) => {
    setEditTarget(card);
    setEditNickname(card.nickname ?? "");
  };

  const submitNickname = () => {
    if (!editTarget) return;
    const trimmed = editNickname.trim();
    updateNicknameMutation.mutate({ id: editTarget.id, nickname: trimmed.length > 0 ? trimmed : null });
  };

  return (
    <Card className="p-6" data-testid="card-host-payment-methods">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Cards on file</h2>
            <p className="text-sm text-gray-500">
              Use one of these cards to fund vendor payouts when you approve a job.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={!stripeReady || readOnly}
          data-testid="button-add-host-card"
        >
          <Plus className="h-4 w-4 mr-1" /> Add card
        </Button>
      </div>

      {!stripeReady && (
        <p className="text-xs text-amber-600 mb-3" data-testid="text-stripe-key-missing">
          {serverKeyMissing
            ? "Card setup is unavailable: Stripe isn't configured on the server yet."
            : "Card setup is unavailable: Stripe publishable key isn't configured for this environment."}
        </p>
      )}

      {cardsQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="text-no-host-cards">
          No saved cards yet. Add one to start approving vendor payments.
        </p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((card) => (
            <li
              key={card.id}
              className={`flex items-center justify-between gap-3 border rounded-md p-3 ${isExpired(card) ? "border-red-200 bg-red-50" : ""}`}
              data-testid={`row-host-card-${card.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard className="h-5 w-5 text-gray-500 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 capitalize" data-testid={`text-host-card-line-${card.id}`}>
                      {formatCardLine(card)}
                    </span>
                    {card.isDefault && (
                      <Badge variant="secondary" className="text-[10px]" data-testid={`badge-host-card-default-${card.id}`}>Default</Badge>
                    )}
                    {isExpired(card) && (
                      <Badge variant="destructive" className="text-[10px] gap-1" data-testid={`badge-host-card-expired-${card.id}`}>
                        <AlertTriangle className="h-3 w-3" /> EXPIRED
                      </Badge>
                    )}
                    {isExpiringSoon(card) && !isExpired(card) && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-[10px]" data-testid={`badge-host-card-expiring-${card.id}`}>
                        Expiring soon
                      </Badge>
                    )}
                    {card.needsReEntry && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-400 text-amber-700 bg-amber-50 gap-1"
                        title="This card is attached to the wrong Stripe customer and will fail at checkout. Remove it and add it again."
                        data-testid={`badge-host-card-needs-reentry-${card.id}`}
                      >
                        <AlertTriangle className="h-3 w-3" /> Needs re-entry
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {card.nickname ? <span className="mr-2">{card.nickname}</span> : null}
                    {formatExpiry(card)}
                  </div>
                  {card.needsReEntry && (
                    <div className="text-xs text-amber-700 mt-1" data-testid={`text-host-card-needs-reentry-${card.id}`}>
                      This card needs to be removed and re-added before it can fund vendor payouts.
                    </div>
                  )}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={readOnly}
                    data-testid={`button-host-card-actions-${card.id}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => openEditNickname(card)}
                    data-testid={`button-edit-nickname-${card.id}`}
                  >
                    <Pencil className="h-4 w-4 mr-2" /> Edit nickname
                  </DropdownMenuItem>
                  {!card.isDefault && (
                    <DropdownMenuItem
                      onClick={() => setDefaultMutation.mutate(card.id)}
                      disabled={setDefaultMutation.isPending}
                      data-testid={`button-set-default-${card.id}`}
                    >
                      <Star className="h-4 w-4 mr-2" /> Set as default
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setDeleteTarget(card)}
                    className="text-red-600"
                    data-testid={`button-host-card-delete-${card.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      )}

      <AddCardDialog open={addOpen} onOpenChange={setAddOpen} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="dialog-delete-host-card">
          <DialogHeader>
            <DialogTitle>Remove this card?</DialogTitle>
            <DialogDescription>
              {deleteTarget && `${formatCardLine(deleteTarget)} will be removed from PadPulse and detached from Stripe. Pending vendor payments tied to this card will be blocked.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="button-cancel-delete-host-card">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-host-card"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Remove card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editTarget}
        onOpenChange={(o) => {
          if (!o) {
            setEditTarget(null);
            setEditNickname("");
          }
        }}
      >
        <DialogContent data-testid="dialog-edit-nickname">
          <DialogHeader>
            <DialogTitle>Edit card nickname</DialogTitle>
            <DialogDescription>
              {editTarget && (
                <>{editTarget.brand?.toUpperCase()} •••• {editTarget.last4}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-card-nickname">Nickname</Label>
            <Input
              id="edit-card-nickname"
              value={editNickname}
              onChange={(e) => setEditNickname(e.target.value)}
              placeholder="e.g. Maintenance Visa"
              maxLength={60}
              data-testid="input-edit-nickname"
            />
            <p className="text-xs text-gray-500">Leave blank to remove the nickname.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditTarget(null);
                setEditNickname("");
              }}
              disabled={updateNicknameMutation.isPending}
              data-testid="button-cancel-edit-nickname"
            >
              Cancel
            </Button>
            <Button
              onClick={submitNickname}
              disabled={updateNicknameMutation.isPending}
              data-testid="button-save-nickname"
            >
              {updateNicknameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Add card dialog: kicks off a SetupIntent, mounts <PaymentElement>,
// confirms on submit, then POSTs the resulting payment_method id.
// ---------------------------------------------------------------------
function AddCardDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { toast } = useToast();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const startSetup = async () => {
    setIsStarting(true);
    try {
      const res = await apiRequest("POST", "/api/app/billing/payment-methods/setup-intent", {});
      const data = await res.json();
      if (!data?.clientSecret) throw new Error("Server did not return a client secret");
      setClientSecret(data.clientSecret);
    } catch (e: any) {
      toast({ title: "Couldn't start card setup", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
      onOpenChange(false);
    } finally {
      setIsStarting(false);
    }
  };

  // Reset whenever the dialog closes.
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) setClientSecret(null);
  };

  // Lazily start the setup when the dialog opens.
  if (open && !clientSecret && !isStarting) {
    startSetup();
  }

  const stripePromise = getStripePromise();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="dialog-add-host-card"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add a card</DialogTitle>
          <DialogDescription>
            Cards are saved with Stripe and used to fund vendor payouts. We never see the card number.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {!stripePromise ? (
            <p className="text-sm text-amber-600">Stripe is not configured.</p>
          ) : !clientSecret ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparing secure card form…
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
              <AddCardForm onDone={() => handleOpenChange(false)} />
            </Elements>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddCardForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [nickname, setNickname] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Stay on this page rather than redirect away. Stripe will only
          // redirect for next-action methods (3DS). For plain cards, the
          // promise resolves with the result inline.
          return_url: `${window.location.origin}/app/account/billing`,
        },
        redirect: "if_required",
      });
      if (confirmError) {
        setError(confirmError.message ?? "Couldn't confirm card.");
        setSubmitting(false);
        return;
      }
      if (!setupIntent || !setupIntent.payment_method || setupIntent.status !== "succeeded") {
        setError("Card setup did not complete. Please try again.");
        setSubmitting(false);
        return;
      }
      const paymentMethodId = typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method.id;
      const saveRes = await apiRequest("POST", "/api/app/billing/payment-methods", {
        paymentMethodId,
        nickname: nickname.trim() || null,
        setAsDefault: makeDefault,
      });
      if (!saveRes.ok) throw new Error(await saveRes.text());
      await queryClient.invalidateQueries({ queryKey: ["/api/app/billing/payment-methods"] });
      toast({ title: "Card saved" });
      onDone();
    } catch (e: any) {
      setError(parseErrorMessage(e, "Couldn't save card."));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1" data-testid="form-add-host-card">
      <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
        <PaymentElement />
        <div>
          <Label htmlFor="host-card-nickname">Nickname (optional)</Label>
          <Input
            id="host-card-nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Maintenance Visa"
            maxLength={60}
            data-testid="input-host-card-nickname"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={makeDefault}
            onChange={(e) => setMakeDefault(e.target.checked)}
            data-testid="checkbox-host-card-default"
          />
          Make this my default card
        </label>
        {error && <p className="text-sm text-red-600" data-testid="text-host-card-error">{error}</p>}
      </div>
      <DialogFooter className="flex-shrink-0 pt-4">
        <Button type="button" variant="outline" onClick={onDone} disabled={submitting} data-testid="button-cancel-add-host-card">
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || submitting} data-testid="button-submit-add-host-card">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save card
        </Button>
      </DialogFooter>
    </form>
  );
}
